
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Search, Calendar,
  ArrowUpDown, Edit2, Users, RefreshCw, Upload, Trash2, Info, CheckCircle2, Tag, Check, Database, FileText, Landmark, Settings, EyeOff, Eye, X, CalendarClock, Zap, ArrowRightCircle, ArrowLeftCircle, Link2
} from 'lucide-react';
import { ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { api } from '../services/api';
import { ReconciliationRecord, DensaiReservation, MatchStatus } from '../types';
import { COLORS } from '../constants';
import UploadModal from './modals/UploadModal';
import ReconciliationEditModal from './modals/ReconciliationEditModal';
import ManualMatchingModal from './modals/ManualMatchingModal';
import ConfirmDialog from './modals/ConfirmDialog';
import DeleteDataModal from './modals/DeleteDataModal';

const TABS = [
  { id: 'all', label: 'すべて' },
  { id: 'matched', label: '照合済' },
  { id: 'review', label: '要確認' },
  { id: 'unmatched', label: '未照合' },
  { id: 'other', label: 'その他' },
  { id: 'fb_pending', label: '入金のみ' },
  { id: 'densai_pending', label: 'でんさい待ち' },
];

const Dashboard: React.FC = () => {
  const [currentMonth, setCurrentMonth] = useState('');
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('all');
  const [data, setData] = useState<ReconciliationRecord[]>([]);
  const [densaiData, setDensaiData] = useState<DensaiReservation[]>([]); 
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: string, direction: 'asc' | 'desc' }>({ key: 'deposit_date', direction: 'desc' });
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadType, setUploadType] = useState<'billing' | 'bank' | 'densai' | null>(null); 
  
  // Loading State Extensions
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [recurringStats, setRecurringStats] = useState({ total: 0, paid: 0, unpaid: 0, paidDetails: [] as string[], unpaidDetails: [] as string[] });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  
  const [editingRecord, setEditingRecord] = useState<ReconciliationRecord | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [manualMatchRecord, setManualMatchRecord] = useState<ReconciliationRecord | null>(null);
  const [isManualMatchModalOpen, setIsManualMatchModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [hideExact, setHideExact] = useState(false);
  const [showBadgeOnly, setShowBadgeOnly] = useState(false);

  // Popover state for payment stats
  const [isPaymentStatsOpen, setIsPaymentStatsOpen] = useState(false);
  const paymentStatsRef = useRef<HTMLDivElement>(null);

  // 仮想スクロール用
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [confirmDialog, setConfirmDialog] = useState<any>({ isOpen: false });

  // タブ切替・検索時にスクロール位置をリセット
  useEffect(() => {
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
  }, [activeTab, searchTerm]);

  // タブ切替時にバッジフィルターをリセット
  useEffect(() => {
    if (activeTab !== 'matched') setShowBadgeOnly(false);
  }, [activeTab]);

  // バッジフィルター切替時にチェックマークをリセット
  useEffect(() => {
    setSelectedIds(new Set());
  }, [showBadgeOnly]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Click outside handler for Payment Stats Popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
        if (paymentStatsRef.current && !paymentStatsRef.current.contains(event.target as Node)) {
            setIsPaymentStatsOpen(false);
        }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchMonths = async (forceSelectMonth?: string) => {
    try {
        const months = await api.reconciliation.getAvailableMonths();
        setAvailableMonths(months);
        if (months.length > 0) {
            if (forceSelectMonth) setCurrentMonth(forceSelectMonth);
            else if (!currentMonth || !months.includes(currentMonth)) setCurrentMonth(months[0]);
        } else {
             const today = new Date();
             const now = `${today.getFullYear()}/${String(today.getMonth() + 1).padStart(2, '0')}`;
             setAvailableMonths([now]);
             setCurrentMonth(now);
        }
    } catch (err) {
        console.error("Failed to fetch months", err);
    }
  };

  useEffect(() => { fetchMonths(); }, []);

  const refreshData = async () => {
    if (!currentMonth) return;
    setIsLoading(true);
    setLoadingMessage("データ読み込み中...");
    setError(null);
    setSelectedIds(new Set()); 
    
    try {
      const overview = await api.reconciliation.getOverview(currentMonth);
      setData(overview.records);
      setRecurringStats(overview.recurringStats);
      setDensaiData(overview.densaiPending);
    } catch (error: any) {
      console.error("Failed to load data", error);
      setError("データの読み込みに失敗しました。");
      setData([]);
    } finally {
      setIsLoading(false);
      setLoadingMessage("");
    }
  };

  const handleUploadSuccess = async (importedMonth?: string) => {
      if (importedMonth) {
          await fetchMonths(importedMonth);
      } else {
          await fetchMonths();
      }
      await refreshData();
  };

  const handleReMatch = async () => {
      setIsLoading(true);
      setLoadingMessage("再照合を実行中...");
      try {
          await api.reconciliation.runAutoMatch();
          await refreshData();
      } catch (e) {
          console.error(e);
          setError("再照合中にエラーが発生しました。");
      } finally {
          setIsLoading(false);
      }
  };

  useEffect(() => { if (currentMonth) refreshData(); }, [currentMonth]);

  const handleOpenUpload = (type: 'billing' | 'bank' | 'densai') => {
    setUploadType(type);
    setIsUploadModalOpen(true);
  };

  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const executeDeleteData = async (month: string, targets: string[]) => {
    setIsLoading(true);
    setLoadingMessage("データ処理中...");
    try {
      await api.data.deleteDataByMonth(month, targets);
      await fetchMonths();
      await refreshData();
      setError("指定されたデータを削除しました。");
    } catch (e: any) {
      console.error(e);
      setError("削除中にエラーが発生しました: " + e.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproveSelected = () => {
      if (selectedIds.size === 0) return;
      // でんさい待ちは対象外
      if (activeTab === 'densai_pending') return;

      setConfirmDialog({
          isOpen: true,
          title: '一括承認',
          message: `選択した ${selectedIds.size} 件のデータを承認します。\nこれらは「完全一致」として確定されます。よろしいですか？`,
          type: 'success',
          confirmText: '一括承認',
          onConfirm: async () => {
              setIsLoading(true);
              setLoadingMessage("一括承認を実行中...");
              try {
                  const targets = data.filter(r => selectedIds.has(r.id));
                  // 並列実行で高速化
                  await Promise.all(targets.map(async (record) => {
                      const diff = Math.abs(record.difference ?? 0);
                      const approvedStatus = diff === 0 ? MatchStatus.EXACT : diff <= 1000 ? MatchStatus.WITH_FEE : MatchStatus.MANUAL_MATCH;
                       if (record.type === 'matched') {
                          await api.reconciliation.updateRecord({ ...record, status: approvedStatus, manually_approved: true });
                        } else if ((record.type === 'deposit' || record.type === 'billing') && record.status === MatchStatus.UNMATCHED) {
                            await api.reconciliation.createMatch({
                              billing_id: record.type === 'billing' ? record.id : undefined,
                              deposit_id: record.type === 'deposit' ? record.id : undefined,
                              client_code: record.client_code,
                              match_type: approvedStatus,
                              manually_approved: true,
                              notes: '一括承認'
                            });
                        }
                  }));
                  await refreshData();
                  setError("一括承認が完了しました。");
              } catch (e) {
                  console.error(e);
                  setError("一括承認中にエラーが発生しました。");
              } finally {
                  setIsLoading(false);
                  setConfirmDialog({ isOpen: false });
              }
          }
      });
  };

  const handleDeleteSelected = () => {
      if (selectedIds.size === 0) return;

      if (activeTab === 'densai_pending') {
          setConfirmDialog({
              isOpen: true,
              title: 'でんさい予約の削除',
              message: `選択した ${selectedIds.size} 件のデータを削除します。\n※誤って登録したデータを削除するための機能です。\n得意先マスタには影響しません。`,
              type: 'danger',
              onConfirm: async () => {
                  setIsLoading(true);
                  setLoadingMessage("データ処理中...");
                  try {
                      await api.densai.deleteMultipleReservations(Array.from(selectedIds));
                      await refreshData();
                      setError("削除が完了しました。");
                  } finally {
                      setIsLoading(false);
                      setConfirmDialog({ isOpen: false });
                  }
              }
          });
          return;
      }
      
      const selectedRecords = data.filter(r => selectedIds.has(r.id));
      
      setConfirmDialog({
          isOpen: true,
          title: 'データ一括削除',
          message: `選択した ${selectedIds.size} 件の消込データをデータベースから完全に削除します。\n※元データ（請求・入金）は削除されますが、得意先マスタは削除されません。`,
          type: 'danger',
          onConfirm: async () => {
              setIsLoading(true);
              setLoadingMessage("データ処理中...");
              try {
                  await api.reconciliation.deleteRecords(selectedRecords);
                  await refreshData();
                  setError("削除が完了しました。");
              } finally {
                  setIsLoading(false);
                  setConfirmDialog({ isOpen: false });
              }
          }
      });
  };

  const handleDeleteSingleDensai = (id: string) => {
      setConfirmDialog({
          isOpen: true,
          title: 'でんさい予約の削除',
          message: 'このデータを削除しますか？\n誤って登録した場合のみ削除してください。',
          type: 'danger',
          onConfirm: async () => {
              setIsLoading(true);
              setLoadingMessage("データ処理中...");
              try {
                  await api.densai.deleteReservation(id);
                  await refreshData();
                  setError("削除が完了しました。");
              } finally {
                  setIsLoading(false);
                  setConfirmDialog({ isOpen: false });
              }
          }
      });
  };

  const handleCarryOver = (record: ReconciliationRecord) => {
      // 入金データの翌月移動（入金日の月へ移動）
      if (record.type === 'deposit') {
          const depMonth = record.deposit_date.replace(/-/g, '/').substring(0, 7);
          const [dy, dm] = depMonth.split('/').map(Number);
          const depMonthLabel = `${dy}年${dm}月`;

          setConfirmDialog({
              isOpen: true,
              title: '入金を本来の月へ移動',
              message: `「${record.remitter_name}」の入金データ（¥${record.deposit_amount.toLocaleString()}）を\n本来の月（${depMonthLabel}）へ移動します。\n\n現在の月からは表示されなくなります。よろしいですか？`,
              type: 'info',
              confirmText: '移動する',
              onConfirm: async () => {
                  setConfirmDialog({ isOpen: false });
                  setIsLoading(true);
                  setLoadingMessage("データ処理中...");
                  try {
                      await api.reconciliation.carryOverDeposit(record.deposit_id || record.id, depMonth);
                      await refreshData();
                      setError("本来の月へ移動しました。");
                  } catch (e) {
                      console.error(e);
                      setError("移動に失敗しました。");
                  } finally {
                      setIsLoading(false);
                  }
              }
          });
          return;
      }

      // 請求データの翌月繰越
      if (!record.billing_id) return;

      const [y, m] = currentMonth.replace('/', '-').split('-').map(Number);
      const nextDate = new Date(y, m, 1);
      const nextMonthStr = `${nextDate.getFullYear()}年${nextDate.getMonth() + 1}月`;

      setConfirmDialog({
          isOpen: true,
          title: '翌月へ繰越',
          message: `「${record.client_name}」の請求データ（¥${record.billing_amount.toLocaleString()}）を\n翌月（${nextMonthStr}）へ移動します。\n\n現在の月からは表示されなくなります。よろしいですか？`,
          type: 'warning',
          confirmText: '繰越を実行',
          onConfirm: async () => {
              setConfirmDialog({ isOpen: false });
              setIsLoading(true);
              setLoadingMessage("データ処理中...");
              try {
                  await api.reconciliation.carryOverBilling(record.billing_id!, record.billing_month);
                  await fetchMonths();
                  await refreshData();
                  setError("翌月へ繰り越しました。");
              } catch (e) {
                  console.error(e);
                  setError("繰越に失敗しました。");
              } finally {
                  setIsLoading(false);
              }
          }
      });
  };

  const handleRevertCarryOver = (record: ReconciliationRecord) => {
      if (record.type === 'billing' && record.moved_from_month) {
          setConfirmDialog({
              isOpen: true,
              title: '前月へ戻す',
              message: `「${record.client_name}」の請求データ（¥${record.billing_amount.toLocaleString()}）を\n元の月（${record.moved_from_month}）へ戻します。よろしいですか？`,
              type: 'warning',
              confirmText: '戻す',
              onConfirm: async () => {
                  setConfirmDialog({ isOpen: false });
                  setIsLoading(true);
                  setLoadingMessage("データ処理中...");
                  try {
                      await api.reconciliation.revertBilling(record.billing_id!, record.moved_from_month!);
                      await fetchMonths();
                      await refreshData();
                      setError("元の月へ戻しました。");
                  } catch (e) {
                      console.error(e);
                      setError("戻す処理に失敗しました。");
                  } finally {
                      setIsLoading(false);
                  }
              }
          });
      } else if (record.type === 'deposit' && record.display_month) {
          setConfirmDialog({
              isOpen: true,
              title: '前月へ戻す',
              message: `「${record.remitter_name}」の入金データ（¥${record.deposit_amount.toLocaleString()}）を\n元の表示に戻します。よろしいですか？`,
              type: 'info',
              confirmText: '戻す',
              onConfirm: async () => {
                  setConfirmDialog({ isOpen: false });
                  setIsLoading(true);
                  setLoadingMessage("データ処理中...");
                  try {
                      await api.reconciliation.revertDeposit(record.deposit_id || record.id);
                      await refreshData();
                      setError("元の月へ戻しました。");
                  } catch (e) {
                      console.error(e);
                      setError("戻す処理に失敗しました。");
                  } finally {
                      setIsLoading(false);
                  }
              }
          });
      }
  };

  const handleSort = (key: string) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };


  const filteredData = useMemo(() => {
    let filtered = data;
    if (activeTab === 'matched') {
        filtered = filtered.filter(r => r.status === MatchStatus.EXACT || r.status === MatchStatus.WITH_FEE || r.status === MatchStatus.MANUAL_MATCH);
    }
    else if (activeTab === 'review') filtered = filtered.filter(r => r.status === MatchStatus.NEEDS_REVIEW);
    else if (activeTab === 'unmatched') filtered = filtered.filter(r => r.status === MatchStatus.UNMATCHED && (r.type === 'billing' || r.type === 'deposit')); 
    else if (activeTab === 'other') filtered = filtered.filter(r => r.is_other || r.status === MatchStatus.OTHER);
    else if (activeTab === 'fb_pending') filtered = filtered.filter(r => r.type === 'deposit' && !r.is_other && r.status === MatchStatus.UNMATCHED);

    // Apply hideExact filter globally if enabled for relevant tabs
    if (hideExact && (activeTab === 'matched' || activeTab === 'all')) {
        filtered = filtered.filter(r => r.status !== MatchStatus.EXACT);
    }

    // 照合済みタブ：同名・別コードのみ表示フィルター
    if (activeTab === 'matched' && showBadgeOnly) {
        filtered = filtered.filter(r => r.candidate_codes && r.candidate_codes.length > 0);
    }

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter(r => 
        r.client_name.toLowerCase().includes(lower) || 
        r.remitter_name.toLowerCase().includes(lower) ||
        r.client_code.includes(lower)
      );
    }

    return [...filtered].sort((a, b) => {
      let aVal: any = a[sortConfig.key as keyof ReconciliationRecord];
      let bVal: any = b[sortConfig.key as keyof ReconciliationRecord];
      if (['billing_amount', 'deposit_amount', 'difference'].includes(sortConfig.key)) {
          aVal = Number(aVal) || 0;
          bVal = Number(bVal) || 0;
      }
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, activeTab, searchTerm, sortConfig, hideExact, showBadgeOnly]);

  const counts = useMemo(() => ({
    all: data.length,
    matched: data.filter(r => r.status === MatchStatus.EXACT || r.status === MatchStatus.WITH_FEE || r.status === MatchStatus.MANUAL_MATCH).length,
    review: data.filter(r => r.status === MatchStatus.NEEDS_REVIEW).length,
    unmatched: data.filter(r => r.status === MatchStatus.UNMATCHED && (r.type === 'billing' || r.type === 'deposit')).length,
    other: data.filter(r => r.is_other || r.status === MatchStatus.OTHER).length,
    fb_pending: data.filter(r => r.type === 'deposit' && !r.is_other && r.status === MatchStatus.UNMATCHED).length,
    densai_pending: densaiData.length,
  }), [data, densaiData]);

  const paymentStatsData = [
    { name: '入金済', value: recurringStats.paid, color: COLORS.success },
    { name: '未入金', value: Math.max(0, recurringStats.total - recurringStats.paid), color: '#f3f4f6' },
  ];

  const measureElement = useCallback((el: Element) => el.getBoundingClientRect().height, []);

  const rowVirtualizer = useVirtualizer({
    count: activeTab === 'densai_pending' ? 0 : filteredData.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 44,
    overscan: 5,
    measureElement,
  });

  const handleManualMatch = async (record: ReconciliationRecord) => {
    setIsLoading(true);
    setLoadingMessage("承認処理中...");
    try {
      const diff = Math.abs(record.difference ?? 0);
      const approvedStatus = diff === 0 ? MatchStatus.EXACT : diff <= 1000 ? MatchStatus.WITH_FEE : MatchStatus.MANUAL_MATCH;
      if (record.type === 'matched') {
        await api.reconciliation.updateRecord({ ...record, status: approvedStatus, manually_approved: true });
      } else if ((record.type === 'deposit' || record.type === 'billing') && record.status === MatchStatus.UNMATCHED) {
          await api.reconciliation.createMatch({
            billing_id: record.type === 'billing' ? record.id : undefined,
            deposit_id: record.type === 'deposit' ? record.id : undefined,
            client_code: record.client_code,
            match_type: approvedStatus,
            manually_approved: true,
            notes: '手動承認'
          });
      }
      await refreshData();
      setError("承認が完了しました。");
    } catch (e) { 
      setError("更新に失敗しました。"); 
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (record: ReconciliationRecord) => { setEditingRecord(record); setIsEditModalOpen(true); };
  const handleOpenManualMatch = (record: ReconciliationRecord) => { setManualMatchRecord(record); setIsManualMatchModalOpen(true); };

  const SortableHeader = ({ label, sortKey, align = 'left', width }: { label: string, sortKey: string, align?: string, width?: string }) => (
    <th 
      className={`p-2.5 ${width ? width : ''} ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'} cursor-pointer hover:bg-gray-100 transition-colors group sticky top-0 bg-gray-50 z-10 border-b`}
      onClick={() => handleSort(sortKey)}
    >
      <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start'}`}>
        <span className="text-[13px] font-black text-gray-800">{label}</span>
        <ArrowUpDown size={12} className={`text-gray-400 ${sortConfig.key === sortKey ? 'text-blue-500' : 'opacity-0 group-hover:opacity-100'}`} />
      </div>
    </th>
  );

  return (
    <div className="h-full flex flex-col space-y-3 relative">
      {isLoading && (
        <div className="absolute inset-0 bg-white/80 z-50 flex flex-col items-center justify-center gap-3 rounded">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
          {loadingMessage && <p className="text-sm font-bold text-gray-600">{loadingMessage}</p>}
        </div>
      )}
      <div className="bg-white p-2.5 rounded shadow-sm border border-gray-200 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
            <span className="text-gray-400 text-[11px] font-black uppercase tracking-wider">対象月</span>
            <select 
                className="border border-gray-300 rounded px-2 py-1 bg-gray-50 focus:ring-1 focus:ring-blue-500 outline-none min-w-[120px] font-bold text-[13px] shadow-sm text-gray-800"
                value={currentMonth}
                onChange={(e) => setCurrentMonth(e.target.value)}
            >
                {availableMonths.map(month => (
                    <option key={month} value={month}>{month.replace('-', '/')}</option>
                ))}
            </select>
        </div>

        <div ref={paymentStatsRef} className="relative">
            <div 
                className="flex items-center gap-3 border-l pl-4 py-0.5 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setIsPaymentStatsOpen(!isPaymentStatsOpen)}
            >
                <div className="flex flex-col">
                    <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-0.5">毎月振込進捗</span>
                    <div className="flex items-center gap-1">
                        <div className="flex items-center gap-0.5">
                            <span className="text-blue-600 font-black text-xl leading-none">{recurringStats.paid}</span>
                            <span className="text-gray-300 font-bold text-sm">/ {recurringStats.total}</span>
                        </div>
                        <div className="h-5 w-5 relative">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={paymentStatsData}
                                        innerRadius={5}
                                        outerRadius={8}
                                        paddingAngle={0}
                                        dataKey="value"
                                        startAngle={90}
                                        endAngle={-270}
                                    >
                                        {paymentStatsData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </div>

            {/* Popover for Recurring Stats Breakdown (Click to toggle) */}
            {isPaymentStatsOpen && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-white shadow-2xl rounded-lg border border-gray-100 z-[100] animate-in fade-in zoom-in-95 duration-200 overflow-hidden ring-1 ring-gray-200">
                    <div className="flex justify-between items-center px-3 py-2 bg-gray-50 border-b border-gray-100">
                        <span className="text-[11px] font-black text-gray-500">進捗詳細リスト</span>
                        <button onClick={() => setIsPaymentStatsOpen(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100">
                            <X size={14} />
                        </button>
                    </div>
                    <div className="flex divide-x divide-gray-100 max-h-72">
                        <div className="w-1/2 flex flex-col">
                             <div className="px-3 py-2 bg-red-50/30 border-b border-gray-100 sticky top-0">
                                 <div className="text-[10px] font-black text-red-500 uppercase tracking-wider flex items-center gap-1">
                                    未入金 ({recurringStats.unpaid})
                                 </div>
                             </div>
                             <ul className="flex-1 overflow-y-auto p-2 space-y-1 scrollbar-thin">
                                {recurringStats.unpaidDetails && recurringStats.unpaidDetails.length > 0 ? (
                                    recurringStats.unpaidDetails.map((name, i) => (
                                        <li key={i} className="flex items-start gap-1.5 text-[11px] font-medium text-gray-700 py-1 px-1 rounded hover:bg-red-50 transition-colors">
                                            <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0 mt-1"></span>
                                            <span className="leading-tight break-all">{name}</span>
                                        </li>
                                    ))
                                ) : (
                                    <li className="text-gray-400 italic text-[10px] p-2 text-center">なし</li>
                                )}
                             </ul>
                        </div>
                         <div className="w-1/2 flex flex-col">
                            <div className="px-3 py-2 bg-blue-50/20 border-b border-gray-100 sticky top-0">
                                <div className="text-[10px] font-black text-blue-500 uppercase tracking-wider flex items-center gap-1">
                                    入金済 ({recurringStats.paid})
                                </div>
                            </div>
                            <ul className="flex-1 overflow-y-auto p-2 space-y-1 scrollbar-thin">
                                {recurringStats.paidDetails && recurringStats.paidDetails.length > 0 ? (
                                    recurringStats.paidDetails.map((name, i) => (
                                        <li key={i} className="flex items-start gap-1.5 text-[11px] font-medium text-gray-500 py-1 px-1 rounded hover:bg-blue-50 transition-colors">
                                            <span className="w-1.5 h-1.5 rounded-full bg-blue-300 flex-shrink-0 mt-1"></span>
                                            <span className="leading-tight break-all">{name}</span>
                                        </li>
                                    ))
                                ) : (
                                    <li className="text-gray-400 italic text-[10px] p-2 text-center">なし</li>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>
            )}
        </div>

        <div className="flex-1 flex justify-end gap-2 items-center">
            <button onClick={handleReMatch} className="p-1.5 text-gray-400 hover:text-blue-600 rounded-full hover:bg-blue-50 transition mr-1" title="再照合">
                <RefreshCw size={18} />
            </button>
            <button onClick={() => handleOpenUpload('billing')} className="px-3 py-1 bg-white border border-gray-300 text-gray-700 rounded text-[13px] font-bold hover:bg-gray-50 shadow-sm">請求取込</button>
            <button onClick={() => handleOpenUpload('bank')} className="px-3 py-1 bg-blue-50 border border-blue-200 text-blue-700 rounded text-[13px] font-bold hover:bg-blue-100 shadow-sm">銀行FB</button>
            <button onClick={() => handleOpenUpload('densai')} className="px-3 py-1 bg-teal-50 border border-teal-200 text-teal-700 rounded text-[13px] font-bold hover:bg-teal-100 shadow-sm">
                でんさい
            </button>
            
            {selectedIds.size > 0 ? (
                <>
                    {activeTab !== 'densai_pending' && (
                        <button onClick={handleApproveSelected} className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded text-[13px] font-black hover:bg-green-700 transition shadow-sm mr-1">
                            <Check size={14} /> 一括承認
                        </button>
                    )}
                    <button onClick={handleDeleteSelected} className="flex items-center gap-1 px-3 py-1 bg-red-600 text-white rounded text-[13px] font-black hover:bg-red-700 transition shadow-sm">
                        <Trash2 size={14} /> 削除（{selectedIds.size}）
                    </button>
                </>
            ) : (
                <button onClick={handleOpenDeleteModal} className="flex items-center gap-1.5 px-3 py-1 bg-white border border-red-300 text-red-600 rounded text-[13px] font-bold hover:bg-red-50 transition shadow-sm">
                    <Trash2 size={14} /> データ削除
                </button>
            )}
        </div>
      </div>

      {error && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-3 py-2 rounded text-[13px] flex items-center justify-between shadow-xs animate-in fade-in slide-in-from-top-1">
            <span className="flex items-center gap-2 font-medium"><Info size={16} /> {error}</span>
            <button onClick={() => setError(null)} className="text-blue-400 hover:text-blue-600 text-lg font-black">×</button>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex flex-col md:flex-row justify-between items-end gap-3">
        <div className="flex items-center gap-4">
            <div className="flex gap-0.5 overflow-x-auto w-full md:w-auto scrollbar-thin">
                {TABS.map(tab => {
                    const count = counts[tab.id as keyof typeof counts];
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-t text-[13px] font-black whitespace-nowrap transition-all border-b-2
                                ${activeTab === tab.id 
                                    ? tab.id === 'densai_pending' ? 'bg-teal-50 text-teal-700 border-teal-600' : 'bg-white text-blue-600 border-blue-600 shadow-xs' 
                                    : 'text-gray-400 border-transparent hover:text-gray-600 hover:bg-gray-50'
                                }
                            `}
                        >
                            {tab.label}
                            <span className={`px-1 py-0.5 rounded-full text-[10px] font-black
                                ${activeTab === tab.id 
                                    ? tab.id === 'densai_pending' ? 'bg-teal-100 text-teal-700' : 'bg-blue-100 text-blue-600' 
                                    : 'bg-gray-100 text-gray-400'}
                            `}>
                                {count}
                            </span>
                        </button>
                    );
                })}
            </div>
            
            {(activeTab === 'matched' || activeTab === 'all') && (
                <button
                    onClick={() => setHideExact(!hideExact)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold transition-all border ${
                        hideExact
                        ? 'bg-blue-50 border-blue-200 text-blue-600'
                        : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                    }`}
                >
                    {hideExact ? <EyeOff size={14}/> : <Eye size={14}/>}
                    完全一致を非表示
                </button>
            )}
            {activeTab === 'matched' && (
                <button
                    onClick={() => setShowBadgeOnly(!showBadgeOnly)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold transition-all border ${
                        showBadgeOnly
                        ? 'bg-orange-50 border-orange-200 text-orange-600'
                        : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                    }`}
                >
                    {showBadgeOnly ? <EyeOff size={14}/> : <Eye size={14}/>}
                    同名・別コードのみ
                </button>
            )}
        </div>

        <div className="relative w-full md:w-64 mb-0.5">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
            <input 
                type="text" 
                placeholder="名称・コード検索" 
                className="w-full pl-9 pr-4 py-1.5 border border-gray-300 rounded-full focus:ring-1 focus:ring-blue-500 outline-none text-[13px] bg-white shadow-inner text-gray-800"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
        </div>
      </div>

      <div className="flex-1 bg-white rounded shadow-sm border border-gray-200 overflow-hidden flex flex-col">
        <div className="flex-1 overflow-auto" ref={scrollContainerRef}>
            {activeTab === 'densai_pending' ? (
                 <table className="w-full text-[13px] text-left whitespace-nowrap border-separate border-spacing-0">
                    <thead className="bg-gray-50 text-gray-600 font-black">
                        <tr>
                            <th className="p-3 w-10 text-center sticky top-0 bg-gray-50 border-b border-gray-200">
                                <input 
                                    type="checkbox" 
                                    checked={densaiData.length > 0 && selectedIds.size === densaiData.length}
                                    onChange={(e) => {
                                        if (e.target.checked) setSelectedIds(new Set(densaiData.map(r => r.id)));
                                        else setSelectedIds(new Set());
                                    }}
                                    className="w-3.5 h-3.5 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
                                />
                            </th>
                            <th className="p-3 border-b border-gray-200 sticky top-0 bg-gray-50">得意先CD</th>
                            <th className="p-3 border-b border-gray-200 sticky top-0 bg-gray-50">得意先名（マスタ）</th>
                            <th className="p-3 border-b border-gray-200 sticky top-0 bg-gray-50">利用者番号</th>
                            <th className="p-3 border-b border-gray-200 sticky top-0 bg-gray-50">支払期日</th>
                            <th className="p-3 border-b border-gray-200 sticky top-0 bg-gray-50 text-right">債権金額</th>
                            <th className="p-3 border-b border-gray-200 sticky top-0 bg-gray-50 text-center">状態</th>
                            <th className="p-3 border-b border-gray-200 sticky top-0 bg-gray-50 text-center">操作</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {densaiData.length === 0 ? (
                            <tr><td colSpan={8} className="p-12 text-center text-gray-300 font-bold italic">入金待ちのでんさい記録はありません</td></tr>
                        ) : (
                            densaiData.map(r => (
                                <tr key={r.id} className={`hover:bg-teal-50/20 transition-colors ${selectedIds.has(r.id) ? 'bg-teal-50/50' : ''}`}>
                                    <td className="p-3 text-center border-b">
                                        <input 
                                            type="checkbox" 
                                            checked={selectedIds.has(r.id)}
                                            onChange={() => {
                                                const next = new Set(selectedIds);
                                                if(next.has(r.id)) next.delete(r.id); else next.add(r.id);
                                                setSelectedIds(next);
                                            }}
                                            className="w-3.5 h-3.5 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
                                        />
                                    </td>
                                    <td className="p-3 border-b font-mono text-gray-800 font-bold">{r.client_code || <span className="text-red-400 text-xs">未登録</span>}</td>
                                    <td className="p-3 border-b font-bold text-gray-800">{r.client_name}</td>
                                    <td className="p-3 border-b font-mono text-gray-600">{r.densai_user_code}</td>
                                    <td className="p-3 border-b font-bold text-gray-800">{r.payment_date}</td>
                                    <td className="p-3 border-b font-black text-right text-gray-800">¥{r.amount.toLocaleString()}</td>
                                    <td className="p-3 border-b text-center">
                                        <span className="px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 text-[10px] font-black">入金待ち</span>
                                    </td>
                                    <td className="p-3 border-b text-center">
                                        <button onClick={() => handleDeleteSingleDensai(r.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded transition" title="削除">
                                            <Trash2 size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                 </table>
            ) : (
                <table className="w-full text-[13px] text-left whitespace-nowrap border-separate border-spacing-0">
                <thead className="bg-gray-50 text-gray-600 font-black">
                    <tr>
                    <th className="p-2.5 w-10 text-center sticky top-0 bg-gray-50 border-b z-10">
                        <input 
                            type="checkbox" 
                            checked={filteredData.length > 0 && selectedIds.size === filteredData.length}
                            onChange={(e) => {
                                if (e.target.checked) setSelectedIds(new Set(filteredData.map(r => r.id)));
                                else setSelectedIds(new Set());
                            }}
                            className="w-3.5 h-3.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                    </th>
                    <SortableHeader label="得意先CD" sortKey="client_code" width="w-20" />
                    <SortableHeader label="得意先名（マスタ）" sortKey="client_name" width="min-w-[180px]" />
                    <SortableHeader label="月" sortKey="billing_month" width="w-16" />
                    <SortableHeader label="振込人名（ソース）" sortKey="remitter_name" width="min-w-[200px]" />
                    <SortableHeader label="請求額" sortKey="billing_amount" align="right" />
                    <SortableHeader label="入金額" sortKey="deposit_amount" align="right" />
                    <SortableHeader label="差額" sortKey="difference" align="right" />
                    <SortableHeader label="入金日" sortKey="deposit_date" />
                    <SortableHeader label="結果" sortKey="status" align="center" />
                    <th className="p-2.5 text-center sticky top-0 bg-gray-50 border-b z-10 font-black text-[13px] text-gray-800">操作</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {filteredData.length === 0 ? (
                        <tr><td colSpan={12} className="p-12 text-center text-gray-300 font-bold italic">表示するデータはありません</td></tr>
                    ) : (
                        <>
                        {rowVirtualizer.getVirtualItems()[0]?.start > 0 && (
                            <tr><td colSpan={12} style={{ height: `${rowVirtualizer.getVirtualItems()[0].start}px`, padding: 0, border: 'none' }} /></tr>
                        )}
                        {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                        const record = filteredData[virtualItem.index];
                        return (
                        <tr key={record.id} data-index={virtualItem.index} ref={rowVirtualizer.measureElement} className={`hover:bg-blue-50/10 transition-colors ${selectedIds.has(record.id) ? 'bg-blue-50/50' : ''}`}>
                            <td className="p-2.5 text-center border-b">
                                <input 
                                    type="checkbox" 
                                    checked={selectedIds.has(record.id)}
                                    onChange={() => {
                                        const next = new Set(selectedIds);
                                        if(next.has(record.id)) next.delete(record.id); else next.add(record.id);
                                        setSelectedIds(next);
                                    }}
                                    className="w-3.5 h-3.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                />
                            </td>
                            <td className="p-2.5 text-gray-800 font-mono text-[11px] border-b">{record.client_code || '-'}</td>
                            <td className="p-2.5 font-bold border-b text-[13px]">
                                <div className="flex items-center gap-1.5 text-gray-800">
                                    <span className="truncate max-w-[160px]">{record.client_name || '-'}</span>
                                    <div className="flex gap-0.5 flex-shrink-0">
                                        {record.is_union && <span title="組合所属" className="text-purple-400"><Users size={12} /></span>}
                                        {record.is_monthly && <span title="毎月振込設定あり" className="text-blue-400"><Calendar size={12} /></span>}
                                    </div>
                                </div>
                            </td>
                            <td className="p-2.5 text-gray-800 text-[11px] border-b">
                                {record.billing_month && record.billing_month !== '-' ? record.billing_month.replace('-', '/') : '-'}
                            </td>
                            <td className="p-2.5 text-gray-800 font-bold font-mono text-[11px] border-b">
                                <div className="flex items-center gap-1.5">
                                    <span className="truncate max-w-[140px]">{record.remitter_name || '-'}</span>
                                </div>
                            </td>
                            <td className="p-2.5 text-right border-b font-bold text-[13px] text-gray-800">
                                {record.billing_amount !== 0 ? `¥${record.billing_amount.toLocaleString()}` : '-'}
                                {record.billing_amounts && record.billing_amounts.length > 1 && (
                                    <div className="mt-0.5 space-y-0.5">
                                        {record.billing_amounts.map((amt, i) => (
                                            <div key={i} className="text-[10px] text-gray-400 font-normal">
                                                └ ¥{amt.toLocaleString()}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </td>
                            <td className="p-2.5 text-right border-b font-bold text-[13px] text-gray-800">
                                {record.deposit_amount !== 0 ? `¥${record.deposit_amount.toLocaleString()}` : '-'}
                            </td>
                            <td className={`p-2.5 text-right border-b font-black text-[13px] ${record.difference === 0 ? 'text-gray-200' : 'text-red-500'}`}>
                                {(record.is_densai && record.billing_amount === 0)
                                    ? '-'
                                    : record.difference !== 0 ? `¥${record.difference.toLocaleString()}` : '0'}
                            </td>
                            <td className="p-2.5 text-gray-800 text-[11px] border-b">
                                {record.deposit_date && record.deposit_date !== '-' ? record.deposit_date.replace(/-/g, '/') : '-'}
                            </td>
                            <td className="p-2.5 text-center border-b">
                                <div className="flex flex-col items-center gap-0.5">
                                    <div className="flex items-center gap-1">
                                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-black inline-block w-20 text-center shadow-xs
                                            ${record.status === MatchStatus.EXACT ? 'bg-green-100 text-green-700' :
                                            record.status === MatchStatus.WITH_FEE ? 'bg-yellow-100 text-yellow-700' :
                                            record.status === MatchStatus.MANUAL_MATCH ? 'bg-blue-100 text-blue-700' :
                                            record.status === MatchStatus.NEEDS_REVIEW ? 'bg-orange-100 text-orange-700' :
                                            record.status === MatchStatus.OTHER ? 'bg-gray-100 text-gray-700' :
                                            'bg-red-50 text-red-600'}
                                        `}>
                                            {record.status}
                                        </span>
                                    </div>
                                    
                                    {record.is_densai && (
                                        <span className="text-[9px] text-teal-600 font-black flex items-center gap-0.5 mt-0.5 animate-in fade-in duration-300" title="でんさい予約消込済">
                                            <Zap size={10} className="fill-teal-100" /> でんさい
                                        </span>
                                    )}

                                    {record.manually_approved && (record.status === MatchStatus.EXACT || record.status === MatchStatus.WITH_FEE || record.status === MatchStatus.MANUAL_MATCH) && (
                                        <span className="text-[9px] text-blue-500 font-black flex items-center gap-0.5 mt-0.5 animate-in fade-in duration-300" title="手動で承認されました">
                                            <CheckCircle2 size={10} /> 手動承認
                                        </span>
                                    )}

                                    {record.notes && record.status === MatchStatus.NEEDS_REVIEW && record.notes.includes('名称類似') && (
                                        <span className="text-[9px] text-orange-500 font-bold mt-0.5" title={record.notes}>
                                            名称推論（要確認）
                                        </span>
                                    )}
                                    {record.candidate_codes && record.candidate_codes.length > 1 && (
                                        <span className="text-[9px] text-purple-600 font-bold mt-0.5" title={`候補: ${record.candidate_codes.join(', ')}`}>
                                            同名・別コード（{record.candidate_codes.length}件）
                                        </span>
                                    )}
                                    {record.billing_count && record.billing_count > 1 && (
                                        <span className="text-[9px] text-blue-600 font-bold mt-0.5">
                                            {record.billing_count}件の請求
                                        </span>
                                    )}
                                </div>
                            </td>
                            <td className="p-2.5 text-center border-b">
                                <div className="flex justify-center gap-1">
                                    {/* 手動照合ボタン：入金・照合済みレコードに表示 */}
                                    {(record.type === 'deposit' || record.type === 'matched') && !record.is_densai && (
                                        <button onClick={() => handleOpenManualMatch(record)} className="p-1 text-blue-500 hover:bg-blue-100 rounded transition" title="手動照合"><Link2 size={14} /></button>
                                    )}
                                    {(activeTab === 'review' || activeTab === 'fb_pending' || record.status === MatchStatus.NEEDS_REVIEW || record.status === MatchStatus.UNMATCHED) && (
                                        <button onClick={() => handleManualMatch(record)} className="p-1 text-green-600 hover:bg-green-100 rounded transition" title="承認"><Check size={14} /></button>
                                    )}
                                    {/* 請求データの繰越ボタン / 前月へ戻すボタン */}
                                    {record.type === 'billing' && record.status === MatchStatus.UNMATCHED && (
                                        record.moved_from_month
                                            ? <button onClick={() => handleRevertCarryOver(record)} className="p-1 text-purple-500 hover:bg-purple-50 rounded transition" title="前月へ戻す"><ArrowLeftCircle size={14} /></button>
                                            : <button onClick={() => handleCarryOver(record)} className="p-1 text-purple-500 hover:bg-purple-50 rounded transition" title="翌月へ繰越"><CalendarClock size={14} /></button>
                                    )}
                                    {/* 入金データの翌月移動ボタン / 前月へ戻すボタン */}
                                    {record.type === 'deposit' && record.status === MatchStatus.UNMATCHED && (
                                        record.display_month
                                            ? <button onClick={() => handleRevertCarryOver(record)} className="p-1 text-orange-500 hover:bg-orange-50 rounded transition" title="前月へ戻す"><ArrowLeftCircle size={14} /></button>
                                            : (record.deposit_date.substring(0, 7).replace('-', '/') > currentMonth.substring(0, 7)) && (
                                                <button onClick={() => handleCarryOver(record)} className="p-1 text-orange-500 hover:bg-orange-50 rounded transition" title="本来の月へ戻す（翌月移動）"><ArrowRightCircle size={14} /></button>
                                            )
                                    )}
                                    <button onClick={() => handleEdit(record)} className="p-1 text-blue-600 hover:bg-blue-100 rounded transition"><Edit2 size={14} /></button>
                                </div>
                            </td>
                        </tr>
                        );
                        })}
                        {(() => {
                            const items = rowVirtualizer.getVirtualItems();
                            const last = items[items.length - 1];
                            const bottomPad = last ? rowVirtualizer.getTotalSize() - last.end : 0;
                            return bottomPad > 0 ? <tr><td colSpan={12} style={{ height: `${bottomPad}px`, padding: 0, border: 'none' }} /></tr> : null;
                        })()}
                        </>
                    )}
                </tbody>
                </table>
            )}
        </div>
        <div className="px-4 py-2 bg-gray-50 border-t text-[11px] font-black text-gray-400 flex justify-between items-center uppercase tracking-widest">
            {activeTab === 'densai_pending' ? (
                <>
                    <span>全 {densaiData.length} 件（未入金）</span>
                    {selectedIds.size > 0 && <span className="text-teal-600">{selectedIds.size} 件選択中</span>}
                </>
            ) : (
                <>
                    <span>全 {filteredData.length} 件</span>
                    {selectedIds.size > 0 && <span className="text-blue-600">{selectedIds.size} 件選択中</span>}
                </>
            )}
        </div>
      </div>

      <UploadModal isOpen={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)} type={uploadType || 'billing'} onSuccess={handleUploadSuccess} />
      <ReconciliationEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        record={editingRecord}
        onSave={async (r) => {
          const updated = { ...r };

          // ステータスを「完全一致」に変更した場合のみ手動承認フラグを立てる
          if (r.status === MatchStatus.EXACT && (!editingRecord || editingRecord.status !== MatchStatus.EXACT)) {
              updated.manually_approved = true;
          } else if (r.status !== MatchStatus.EXACT) {
              updated.manually_approved = false;
          }

          if (r.type === 'billing' || r.type === 'deposit') {
            // 金額・メモ変更を元レコードに反映
            await api.reconciliation.updateSourceRecord(r);
            if (r.status !== MatchStatus.UNMATCHED) {
              await api.reconciliation.createMatch({
                billing_id: r.type === 'billing' ? r.id : undefined,
                deposit_id: r.type === 'deposit' ? r.id : undefined,
                client_code: r.client_code,
                match_type: r.status,
                manually_approved: updated.manually_approved,
                notes: r.notes
              });
            }
          } else {
            await api.reconciliation.updateRecord(updated);
          }

          await refreshData();
        }}
        onUnlink={async (r) => {
          setIsLoading(true);
          setLoadingMessage('紐付けを解除中...');
          try {
            await api.reconciliation.unlinkMatch(r);
            await refreshData();
            setError('紐付けを解除しました。');
          } catch (e) {
            console.error(e);
            setError('紐付け解除に失敗しました。');
          } finally {
            setIsLoading(false);
          }
        }}
      />
      <ManualMatchingModal
        isOpen={isManualMatchModalOpen}
        onClose={() => setIsManualMatchModalOpen(false)}
        record={manualMatchRecord}
        currentMonth={currentMonth}
        onComplete={refreshData}
      />
      <ConfirmDialog {...confirmDialog} onCancel={() => setConfirmDialog({isOpen: false})} />
      <DeleteDataModal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} availableMonths={availableMonths} onDelete={executeDeleteData} />
    </div>
  );
};

export default Dashboard;
